import React from 'react';
import BottleScene from '../DigitalTwin/BottleScene';
import JoinGuardianModal from '../Guardians/JoinGuardianModal';
import { ArrowRight } from 'lucide-react';

/**
 * ProductHero — § I of the new product-led homepage.
 *
 * The brand fades back. The product leads. Split layout:
 *   LEFT (col-span-7)  · the 3D Miron-glass bottle (slow auto-rotate)
 *                       with the new gold (Surya) ferrule + silver
 *                       (Chandra) lock ring pump head — exactly the
 *                       physical artefact we ship.
 *   RIGHT (col-span-5) · a tiny gold overline "VEDIC BIOPHYSICS",
 *                       a small but commanding serif title
 *                       "Just Make It Exist First.", a single
 *                       physical-spec line (vessel · contents · soil),
 *                       a macro ingredient still (kewra-flower), and
 *                       the primary CTA into the Guardian modal.
 *
 * No philosophy paragraphs. No long sub-copy. The product image is
 * the argument; the typography just labels it.
 */

const ProductHero = ({ testId = 'home-product-hero' }) => {
  const [joinOpen, setJoinOpen] = React.useState(false);

  return (
    <section
      data-testid={testId}
      className="relative w-full"
      style={{
        background:
          'radial-gradient(ellipse at 30% 20%, #F4F8F9 0%, #E6EEF1 60%, #C1D7E2 100%)',
      }}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-28 md:pt-36 pb-20 md:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16 items-center">

        {/* LEFT — the 3D vessel */}
        <div className="lg:col-span-7 relative">
          <div
            data-testid={`${testId}-bottle-stage`}
            className="relative w-full max-w-[640px] mx-auto aspect-square"
          >
            <BottleScene fill={45} />
            {/* Faint witness ring under the vessel */}
            <span
              aria-hidden
              className="pointer-events-none absolute -inset-8 rounded-full"
              style={{
                background:
                  'radial-gradient(ellipse at center bottom, rgba(212,175,90,0.22) 0%, transparent 60%)',
              }}
            />
          </div>

          {/* Tiny corner spec strip */}
          <p
            data-testid={`${testId}-vessel-spec`}
            className="absolute left-2 top-2 md:left-4 md:top-4 font-mono text-[10px] tracking-[0.32em] uppercase text-[#7C9CB1] font-semibold"
          >
            SH-042-0188 · 251 ml · Miron Glass
          </p>
          <p
            data-testid={`${testId}-vessel-origin`}
            className="absolute right-2 bottom-2 md:right-4 md:bottom-4 font-mono text-[10px] tracking-[0.32em] uppercase text-[#7C9CB1]"
          >
            Heavener, Oklahoma · Lot 042
          </p>
        </div>

        {/* RIGHT — quiet typography + macro still + CTA */}
        <div className="lg:col-span-5 reveal-up">
          <p
            data-testid={`${testId}-overline`}
            className="font-mono text-[10px] md:text-[11px] tracking-[0.42em] uppercase font-semibold text-[#D4AF5A] flex items-center gap-3 mb-6"
          >
            <span aria-hidden className="inline-block w-7 h-px bg-[#D4AF5A]" />
            Vedic Biophysics
          </p>

          <h1
            data-testid={`${testId}-title`}
            className="font-serif font-light text-[#3C5260] leading-[0.96] tracking-[-0.01em]"
            style={{ fontSize: 'clamp(40px, 4.6vw, 72px)' }}
          >
            Just make it
            <br />
            <em className="text-[#7C9CB1]">exist</em> first.
          </h1>

          <p
            data-testid={`${testId}-physical-spec`}
            className="mt-7 font-mono text-[11px] md:text-[12px] tracking-[0.3em] uppercase text-[#7C9CB1] flex flex-wrap items-center gap-x-3 gap-y-2"
          >
            <span>251 ml</span>
            <span aria-hidden className="text-[#D4AF5A]">·</span>
            <span>100% Kewra Hydrosol</span>
            <span aria-hidden className="text-[#D4AF5A]">·</span>
            <span>Miron Glass</span>
            <span aria-hidden className="text-[#D4AF5A]">·</span>
            <span className="font-semibold text-[#3C5260]">Heavener, OK</span>
          </p>

          {/* Macro ingredient still — kewra flower in close-up */}
          <figure
            data-testid={`${testId}-ingredient-still`}
            className="mt-9 md:mt-10 relative w-full max-w-[420px] overflow-hidden border border-[#C1D7E2] bg-[#0E1A22]"
          >
            <img
              src="/atelier/kewra-flower.png"
              alt="Macro of a Pandanus (Kewra) flower with dew, the sole botanical witnessed in this vessel"
              className="w-full h-auto object-cover"
              loading="eager"
            />
            <figcaption className="absolute left-0 right-0 bottom-0 px-4 py-3 backdrop-blur-md bg-[#0E1A22]/60 font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F8F9]/85 flex items-center gap-2">
              <span aria-hidden className="inline-block w-1.5 h-1.5 rounded-full bg-[#D4AF5A]" />
              The single witnessed botanical
            </figcaption>
          </figure>

          {/* Primary CTA */}
          <button
            type="button"
            onClick={() => setJoinOpen(true)}
            data-testid={`${testId}-cta`}
            className="group mt-9 inline-flex items-center gap-3 px-8 py-4 font-mono text-[11px] md:text-[12px] tracking-[0.32em] uppercase font-semibold text-[#1A0F26] bg-[#D4AF5A] hover:bg-[#E8C977] transition-colors shadow-[0_22px_60px_-22px_rgba(212,175,90,0.55)]"
          >
            Reserve a vessel
            <ArrowRight
              size={14}
              strokeWidth={1.8}
              className="transition-transform duration-500 group-hover:translate-x-0.5"
            />
          </button>
          <p className="mt-3 font-mono text-[10px] tracking-[0.32em] uppercase text-[#7C9CB1]/70">
            1 vessel · 1 guardian · 1 record on chain
          </p>
        </div>
      </div>

      {/* Inline Guardian capture */}
      <JoinGuardianModal
        open={joinOpen}
        onClose={() => setJoinOpen(false)}
        testId={`${testId}-join-modal`}
        source="home_product_hero"
      />
    </section>
  );
};

export default ProductHero;
